import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert, useTrack } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

// change_status is not offered by the macro builder, but the API accepts it and
// it resolves the conversation just like resolve_conversation does. Its param is
// stored as raw JSON, so the status can be the enum name or its integer value.
const RESOLVED_STATUSES = ['resolved', 1];

// NOOVI: acoes que NAO podem rodar sem o agente. O backend nao tem como
// inventar valor de venda nem motivo de perda; no macro esses campos ficam
// vazios de proposito (`inputType: null`), porque um numero fixo salvo no editor
// estaria errado em todo negocio que nao fosse aquele. Entao o macro para, o
// modal correspondente abre, e o macro so segue depois.
const DEAL_ACTIONS = {
  mark_pipeline_card_won: 'won',
  mark_pipeline_card_lost: 'lost',
};

const dealActionOf = macro => {
  const acao = macro.actions?.find(
    ({ action_name: name }) => DEAL_ACTIONS[name]
  );
  return acao ? DEAL_ACTIONS[acao.action_name] : null;
};

const resolvesConversation = macro =>
  macro.actions.some(
    ({ action_name: name, action_params: params }) =>
      name === 'resolve_conversation' ||
      (name === 'change_status' && RESOLVED_STATUSES.includes(params?.[0]))
  );

/**
 * Runs a macro against a conversation, holding back the ones that resolve it until
 * the required custom attributes are filled in.
 *
 * `execute` returns the attributes to prompt for when the caller has to open the
 * modal first, and null once the macro has been handed off.
 */
export function useMacroExecution() {
  const store = useStore();
  const { t } = useI18n();
  const { checkMissingAttributes } = useConversationRequiredAttributes();

  const conversationById = useMapGetter('getConversationById');

  const executingMacroId = ref(null);
  const pendingExecution = ref(null);

  const customAttributesFor = conversationId =>
    conversationById.value(conversationId)?.custom_attributes || {};

  const runMacro = async (
    { macro, conversationId },
    skippedResolve = false
  ) => {
    try {
      executingMacroId.value = macro.id;
      await store.dispatch('macros/execute', {
        macroId: macro.id,
        conversationIds: [conversationId],
      });
      useTrack(CONVERSATION_EVENTS.EXECUTED_A_MACRO);
      useAlert(
        skippedResolve
          ? t('MACROS.EXECUTE.EXECUTED_WITHOUT_RESOLVING')
          : t('MACROS.EXECUTE.EXECUTED_SUCCESSFULLY')
      );
    } catch (error) {
      useAlert(t('MACROS.ERROR'));
    } finally {
      executingMacroId.value = null;
    }
  };

  const execute = (macro, conversationId) => {
    const execution = { macro, conversationId };

    // O prompt de negocio vem ANTES do de atributos: fechar a venda e a acao
    // que o agente disparou conscientemente, e o modal dela ja carrega o
    // contexto do card. Inverter faria o agente responder atributos para so
    // entao descobrir que ainda precisa preencher o valor.
    const dealType = dealActionOf(macro);
    if (dealType) {
      pendingExecution.value = execution;
      return { type: 'deal', dealType, conversationId };
    }

    if (!resolvesConversation(macro)) {
      runMacro(execution);
      return null;
    }

    const customAttributes = customAttributesFor(conversationId);
    const { hasMissing, missing } = checkMissingAttributes(customAttributes);
    if (!hasMissing) {
      runMacro(execution);
      return null;
    }

    pendingExecution.value = execution;
    return { type: 'attributes', missing, customAttributes };
  };

  // O modal ja fechou o card com os valores reais. O macro roda em seguida e a
  // acao `mark_pipeline_card_*` vira no-op sozinha, porque o servico ja tem
  // `next if card.won?` / `next if card.lost?` — sem duplo fechamento e sem
  // precisar de um sinal "pula essa acao".
  const submitPendingDeal = () => {
    const execution = pendingExecution.value;
    if (!execution) return;

    pendingExecution.value = null;
    runMacro(execution);
  };

  // Desistir do modal CANCELA o macro inteiro, e isso e deliberado. O prompt de
  // atributos pode ser dispensado porque o backend degrada bem (deixa a conversa
  // sem resolver); aqui nao: rodar assim mandaria a acao de fechamento sem valor,
  // que o guard de `require_won_value?` recusa dentro de um rescue — falha
  // silenciosa. E as outras acoes do macro (resolver, agradecer a compra)
  // pressupoem a venda fechada.
  const dismissPendingDeal = () => {
    if (!pendingExecution.value) return;

    pendingExecution.value = null;
    useAlert(t('MACROS.EXECUTE.CANCELLED_DEAL'));
  };

  const submitPendingAttributes = async ({ attributes }) => {
    const execution = pendingExecution.value;
    pendingExecution.value = null;

    try {
      await store.dispatch('updateCustomAttributes', {
        conversationId: execution.conversationId,
        customAttributes: {
          ...customAttributesFor(execution.conversationId),
          ...attributes,
        },
      });
    } catch (error) {
      useAlert(t('CUSTOM_ATTRIBUTES.FORM.UPDATE.ERROR'));
      return;
    }

    runMacro(execution);
  };

  // Dismissing the modal still runs the macro, the backend leaves the
  // conversation unresolved while the required attributes are empty.
  const dismissPendingAttributes = () => {
    if (!pendingExecution.value) return;

    runMacro(pendingExecution.value, true);
    pendingExecution.value = null;
  };

  return {
    executingMacroId,
    execute,
    submitPendingAttributes,
    dismissPendingAttributes,
    submitPendingDeal,
    dismissPendingDeal,
  };
}
